import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Button,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Security,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  ExpandMore,
  ExpandLess,
  RefreshRounded,
  Download,
  Assessment,
  Policy,
  VpnKey,
  Lock,
  Timer,
  Speed,
  BugReport,
  Schedule,
  Info,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface ComplianceCheck {
  category: string;
  description: string;
  status: 'passed' | 'warning' | 'failed';
  details?: string;
}

interface ComplianceReport {
  compliance_level: string;
  scan_date: string;
  critical_issues: string[];
  warnings: string[];
  passed_checks: string[];
  total_checks: number;
  critical_count: number;
  warning_count: number;
  passed_count: number;
  next_steps: string[];
}

interface SecurityEvent {
  id: number;
  event_type: string;
  severity: string;
  identifier: string;
  reason: string;
  created_at: string;
  resolved: boolean;
}

interface AuditLog {
  id: number;
  event_type: string;
  username: string;
  ip_address: string;
  endpoint: string;
  success: boolean;
  message: string;
  created_at: string;
}

const PCICompliance: React.FC = () => {
  const { member } = useAuth();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<ComplianceReport | null>(null);
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});
  const [showDetails, setShowDetails] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchComplianceData();
  }, []);

  const fetchComplianceData = async () => {
    setLoading(true);
    try {
      // Fetch compliance report using api service
      const reportResponse = await api.get('/endpoints/pci-compliance.php?action=report');
      if (reportResponse.data.success) {
        setReport(reportResponse.data.data);
      } else {
        console.error('Report fetch failed:', reportResponse.data.message);
      }

      // Fetch recent security events
      const eventsResponse = await api.get('/endpoints/pci-compliance.php?action=security-events');
      if (eventsResponse.data.success) {
        setSecurityEvents(eventsResponse.data.data);
      }

      // Fetch recent audit logs
      const auditResponse = await api.get('/endpoints/pci-compliance.php?action=audit-logs');
      if (auditResponse.data.success) {
        setAuditLogs(auditResponse.data.data);
      }
    } catch (error) {
      console.error('Error fetching compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runComplianceScan = async () => {
    setRefreshing(true);
    try {
      console.log('[PCI Scan] Starting compliance scan...');
      
      // Use GET for scan action (it's a read operation that generates a report)
      const response = await api.get('/endpoints/pci-compliance.php?action=scan');
      
      console.log('[PCI Scan] Response:', response.data);
      
      if (response.data.success || response.data.report) {
        await fetchComplianceData();
        alert('Compliance scan completed successfully!');
      } else {
        console.error('Scan failed:', response.data.message);
        alert(`Scan failed: ${response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error running compliance scan:', error);
      alert('Failed to run compliance scan. Check console for details.');
    } finally {
      setRefreshing(false);
    }
  };

  const downloadReport = async () => {
    try {
      const response = await api.get('/endpoints/pci-compliance.php?action=download', {
        responseType: 'blob',
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pci-compliance-report-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading report:', error);
    }
  };

  const getComplianceColor = (level: string) => {
    switch (level) {
      case 'COMPLIANT':
        return '#10b981';
      case 'PARTIALLY_COMPLIANT':
        return '#f59e0b';
      case 'NON_COMPLIANT':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getComplianceProgress = () => {
    if (!report) return 0;
    return Math.round((report.passed_count / report.total_checks) * 100);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          PCI DSS Compliance Dashboard
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshRounded />}
            onClick={runComplianceScan}
            disabled={refreshing}
            sx={{ mr: 2 }}
          >
            {refreshing ? 'Scanning...' : 'Run Scan'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={downloadReport}
          >
            Download Report
          </Button>
        </Box>
      </Box>

      {/* Compliance Status Card */}
      <Card sx={{ mb: 3, background: report ? `linear-gradient(135deg, ${getComplianceColor(report.compliance_level)}22, transparent)` : undefined }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center">
                <Security sx={{ fontSize: 48, color: report ? getComplianceColor(report.compliance_level) : '#6b7280', mr: 2 }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {report?.compliance_level.replace(/_/g, ' ') || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last Scan: {report ? new Date(report.scan_date).toLocaleString() : 'Never'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Compliance Score
                </Typography>
                <Box display="flex" alignItems="center">
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={getComplianceProgress()} 
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                  <Typography variant="h6" fontWeight="bold">
                    {getComplianceProgress()}%
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <CheckCircle sx={{ color: '#10b981' }} />
                    <Typography variant="h6">{report?.passed_count || 0}</Typography>
                    <Typography variant="caption">Passed</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Warning sx={{ color: '#f59e0b' }} />
                    <Typography variant="h6">{report?.warning_count || 0}</Typography>
                    <Typography variant="caption">Warnings</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <ErrorIcon sx={{ color: '#ef4444' }} />
                    <Typography variant="h6">{report?.critical_count || 0}</Typography>
                    <Typography variant="caption">Critical</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)}>
          <Tab label="Compliance Checks" />
          <Tab label="Security Events" />
          <Tab label="Audit Logs" />
          <Tab label="Configuration" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Grid container spacing={3}>
          {/* Infrastructure Security Summary */}
          {report?.passed_checks?.some(check => check.includes('Infrastructure Security')) && (
            <Grid item xs={12}>
              <Alert severity="success" icon={<Lock />}>
                <AlertTitle>Infrastructure Security Hardening Completed (October 6, 2025)</AlertTitle>
                <Typography variant="body2" gutterBottom>
                  Server-level security measures have been implemented and verified:
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 1, bgcolor: 'success.50', borderRadius: 1 }}>
                      <Typography variant="caption" display="block" color="success.dark" fontWeight="bold">
                        ✓ Firewall Configured
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        UFW with IP whitelisting
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 1, bgcolor: 'success.50', borderRadius: 1 }}>
                      <Typography variant="caption" display="block" color="success.dark" fontWeight="bold">
                        ✓ SSH Hardened
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Weak algorithms removed
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 1, bgcolor: 'success.50', borderRadius: 1 }}>
                      <Typography variant="caption" display="block" color="success.dark" fontWeight="bold">
                        ✓ Server Security
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        PHP/Apache hardened
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 1, bgcolor: 'success.50', borderRadius: 1 }}>
                      <Typography variant="caption" display="block" color="success.dark" fontWeight="bold">
                        ✓ HSTS Enabled
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Force HTTPS with preload
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Alert>
            </Grid>
          )}

          {/* Critical Issues */}
          {report?.critical_issues && report.critical_issues.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="error">
                <AlertTitle>Critical Issues - Immediate Action Required</AlertTitle>
                <List dense>
                  {report.critical_issues.map((issue, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <ErrorIcon color="error" />
                      </ListItemIcon>
                      <ListItemText primary={issue} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            </Grid>
          )}

          {/* Warnings */}
          {report?.warnings && report.warnings.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                <AlertTitle>Warnings - Should Be Addressed</AlertTitle>
                <List dense>
                  {report.warnings.map((warning, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Warning color="warning" />
                      </ListItemIcon>
                      <ListItemText primary={warning} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            </Grid>
          )}

          {/* Passed Checks */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">Passed Checks</Typography>
                  <IconButton onClick={() => toggleSection('passed')}>
                    {expandedSections.passed ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={expandedSections.passed}>
                  <List dense>
                    {report?.passed_checks?.map((check, index) => {
                      // Check if this is an infrastructure header
                      const isInfraHeader = check.includes('Infrastructure Security (Manual Configuration)');
                      // Check if this is an infrastructure sub-item (starts with tree characters)
                      const isInfraSub = check.match(/^[├└]─/);

                      if (isInfraHeader) {
                        return (
                          <React.Fragment key={index}>
                            <Divider sx={{ my: 2 }} />
                            <ListItem>
                              <ListItemIcon>
                                <Lock color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={check.replace(':', '')}
                                primaryTypographyProps={{ fontWeight: 'bold', color: 'primary.main' }}
                              />
                            </ListItem>
                          </React.Fragment>
                        );
                      } else if (isInfraSub) {
                        return (
                          <ListItem key={index} sx={{ pl: 6 }}>
                            <ListItemText
                              primary={check}
                              primaryTypographyProps={{
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                                color: 'text.secondary'
                              }}
                            />
                          </ListItem>
                        );
                      } else {
                        return (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <CheckCircle color="success" />
                            </ListItemIcon>
                            <ListItemText primary={check} />
                          </ListItem>
                        );
                      }
                    })}
                  </List>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>

          {/* Next Steps */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recommended Next Steps
                </Typography>
                <List>
                  {report?.next_steps?.map((step, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Info color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={step} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {selectedTab === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Event Type</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Identifier</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {securityEvents.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>{event.event_type}</TableCell>
                  <TableCell>
                    <Chip 
                      label={event.severity} 
                      color={getSeverityColor(event.severity) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{event.identifier}</TableCell>
                  <TableCell>{event.reason}</TableCell>
                  <TableCell>{new Date(event.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip 
                      label={event.resolved ? 'Resolved' : 'Open'} 
                      color={event.resolved ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selectedTab === 2 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Event</TableCell>
                <TableCell>User</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>{log.event_type}</TableCell>
                  <TableCell>{log.username || 'Anonymous'}</TableCell>
                  <TableCell>{log.ip_address}</TableCell>
                  <TableCell>{log.endpoint}</TableCell>
                  <TableCell>{log.message}</TableCell>
                  <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip 
                      label={log.success ? 'Success' : 'Failed'} 
                      color={log.success ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selectedTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Lock sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Password Policy
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Minimum Length" secondary="12 characters" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Complexity" secondary="Uppercase, lowercase, numbers, special chars" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Expiration" secondary="90 days" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="History" secondary="Cannot reuse last 4 passwords" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Timer sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Session Timeout
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Payment Pages" secondary="15 minutes" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Admin Pages" secondary="30 minutes" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Idle Timeout" secondary="20 minutes" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="General Pages" secondary="60 minutes" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Speed sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Rate Limiting
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Payment Endpoints" secondary="10 req/min, 100 req/hour" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Authentication" secondary="5 attempts/hour" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Lockout Duration" secondary="30-60 minutes" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="API Endpoints" secondary="60 req/min, 1000 req/hour" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Audit & Monitoring
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Log Retention" secondary="1 year (PCI requirement)" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Payment Logs" secondary="3 years retention" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Real-time Alerts" secondary="Critical & High severity" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Suspicious Activity" secondary="Auto-detection enabled" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default PCICompliance;